import type { GameStatusChangeReason } from '@dayon/contracts';

import { codeAsWords } from '../../lib/audit-words.ts';
import { CATALOGUE_REASONS } from './catalogue-write.ts';
import { BRAND_REASONS } from './game-brands.ts';
import { PROVIDER_REASONS } from './provider-status.ts';

/**
 * Why a game changed status, mirroring `gameStatusChangeReason` in the contract.
 *
 * A closed set on both sides. The configuration log is what an operator reads
 * six months later to find out why a game went dark, and "how often did a
 * provider incident cost us a shelf this quarter" is a question prose cannot
 * answer. Each code reads in both directions — a window ends the same way it
 * began — so the same list serves Enable, Maintenance and Disable.
 */
export const STATUS_REASONS = [
  { code: 'SCHEDULED_MAINTENANCE', label: 'Scheduled maintenance window' },
  { code: 'PROVIDER_INCIDENT', label: 'Provider incident or failed integration test' },
  { code: 'COMPLIANCE_HOLD', label: 'Compliance or certification hold' },
  { code: 'RTP_DEVIATION', label: 'Measured RTP deviating from declared' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
  { code: 'CATALOGUE_LAUNCH', label: 'Catalogue launch or relaunch' },
] as const satisfies readonly { code: GameStatusChangeReason; label: string }[];

/**
 * Every reason the Configuration log can hold, by the label its dialog offered.
 *
 * The log merges five kinds of change, and each is written with a reason from its own closed list:
 * game status, provider status, brand availability, and catalogue edits (a new title or provider, a
 * configuration edit, a picture). The Reason column used to know only the status list, so every
 * catalogue edit read "CATALOGUE_CORRECTION" where the operator had picked "Correction to the
 * record" (module walk, 30 Oct 2026). The lists share codes with the same labels on purpose
 * (catalogue-write.ts), so one map serves them all.
 */
const LABELS: ReadonlyMap<string, string> = new Map(
  [...STATUS_REASONS, ...CATALOGUE_REASONS, ...BRAND_REASONS, ...PROVIDER_REASONS].map((r) => [r.code, r.label]),
);

/** The label for a reason code; a code no list knows is written as words, never as the code. */
export function logReasonLabel(code: string): string {
  return LABELS.get(code) ?? codeAsWords(code);
}
